import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import styles from "./RightScrollable.module.scss";

// Container displaying its children overflowing to the right of the screen.
// * it passes a navigation prop so they can display it within themselves.
// * when scrolling, it snaps to the nearest child once the scrolling is done.
// * keeping the finger on the screen will prevent this snapping.
const RightScrollable = (props, ref) => {
  const {
    children,
    snapingDelayMSAfterScrollEvent = 100,
    mobileWidthCutoff = 700,
    thinDesktopWidthCutoff = null,
  } = props;

  const windowWidth = useSelector((state) => state.getIn(["ui", "windowWidth"]));
  // each child will take almost all the viewport width, and they are scrollable.
  const isMobile = (windowWidth) => windowWidth < mobileWidthCutoff;
  // Optionnal thin desktop mode where all children are displayed tightly
  // within the parent's width. Will only work when children are few.
  const isThinDesktop = (windowWidth) => thinDesktopWidthCutoff && !isMobile(windowWidth)
    && windowWidth < thinDesktopWidthCutoff;

  const localRef = useRef();
  const baseRef = ref || localRef;
  // snapping points corresponding to each child's offsetLeft
  const [snaps, setSnaps] = useState([]);
  const [currentOffsetLeft, setCurrenOffsetLeft] = useState(0);
  // we need a timer in order to let scroll events flow until the last one
  // (e.g. the user could swipe strongly and the browser will scroll for a
  // little while before stopping, and we snap only then).
  const [snapingTimerId, setSnapingTimerId] = useState(0);
  // We also keep track of mobile swiping just to avoid snaping while the
  // user is touching the screen (even if he stopped moving and thus scrolling)
  const [swipeFinished, setSwipeFinished] = useState(true);
  const handleTouchStart = () => setSwipeFinished(false);
  const handleTouchEnd = () => setSwipeFinished(true);

  // Register event listener for scroll events, only in mobile
  useEffect(() => {
    if (!isMobile(windowWidth)) {
      // No snapping
      return
    }

    const handleScroll = (event) => {
      setCurrenOffsetLeft(event.target.scrollLeft);
    }

    baseRef.current.addEventListener("scroll", handleScroll);
    
    return () => baseRef.current.removeEventListener("scroll", handleScroll);
  }, [windowWidth, baseRef && baseRef.current]);

  useEffect(() => {
    // On offset update, clear any planned snaping as the user scrolled more
    clearTimeout(snapingTimerId);

    // Ignore snaping as long as the user is swiping (true for mobile only)
    if (swipeFinished && baseRef.current) {
      setSnapingTimerId(setTimeout(() => snap(snaps, currentOffsetLeft, baseRef.current), snapingDelayMSAfterScrollEvent));
    }
  }, [currentOffsetLeft, swipeFinished]);

  const baseClasses = [
    styles.module,
    props.className,
    isMobile(windowWidth) ? styles.mobile : null,
    isThinDesktop(windowWidth) ? styles.thinDesktop : null,
  ];

  // Update list of snapping points (childrens' offsetLefts) on baseRef's
  // scrollwidth update (works for window resize and children changes too).
  useEffect(() => {
    if (children && children.length > 0 && baseRef.current) {
      const padding = 16; // TODO
      if (isMobile(windowWidth)) {
        // Ignore elements currently hidden
        
        const visibleChildrenOffsets = [...baseRef.current.childNodes].reduce((acc, child) => {
          const rect = child.getBoundingClientRect();
          if (rect.width !== 0) {
            acc.push(child.offsetLeft - padding);
          }

          return acc;
        }, []);

        setSnaps(visibleChildrenOffsets);
      }
    }
  }, [baseRef.current && baseRef.current.scrollWidth, windowWidth]);

  // Build a list of div for navigation that's going to be given to children 
  // components, which will be in charge of styling it.
  const handleClick = (index) => baseRef.current && baseRef.current.scrollTo({
    left: snaps[index],
    behavior: 'smooth',
  });
  const childrenWithNav = isMobile(windowWidth) && children.map((child, index) => {
    if (!child) {
      return null; // Children can be undefined
    }
    return React.cloneElement(child, {
      key: index,
      navigation: buildNavigation(children, index, handleClick),
    })
  });

  const customStyles = {
    ...props.style,
    "--mobileHWRatio": props.mobileHWRatio,
    "--numberOfChildren": children ? children.length : 0,
  };

  return (
    <div className={baseClasses.join(" ")} ref={baseRef}
      onMouseDown={handleTouchStart} onMouseUp={handleTouchEnd}
      onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}
      style={customStyles}>
      { childrenWithNav || children }
    </div>
  );
}

const buildNavigation = (children, currentChildIndex, handleClick) => {
  return children.map((_, navIndex) => {
    const dotClass = navIndex === currentChildIndex ? "current" : null;
    return <div key={navIndex} className={dotClass} onClick={() => handleClick(navIndex)}></div>;
  });
}

const snap = (snaps, currentScrollLeft, parent) => {
  if (snaps.length <= 1) {
    return;
  }

  let nearestSnap = null;
  let nearestDistance = Number.POSITIVE_INFINITY;
  for (let i = 0; i < snaps.length; ++i) {
    const distance = Math.abs(snaps[i] - currentScrollLeft);
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearestSnap = snaps[i];
    }
    else {
      break; // the list is ordered
    }
  }

  parent.scrollTo({
    left: nearestSnap,
    behavior: 'smooth',
  });
}


export default React.forwardRef(RightScrollable);